'use client';

import { useEffect } from 'react';
import Script from 'next/script';

/**
 * Google Analytics z Consent Mode v2 zintegrowanym z CookieConsent.
 *
 * Działanie:
 *   1. Skrypt gtag.js ładuje się zawsze, ale GA startuje z `analytics_storage: 'denied'`.
 *   2. Tracking aktywuje się TYLKO po wyrażeniu zgody w bannerze (event `cookieConsentChanged`
 *      z `detail.analytics === true`) lub jeśli zgoda została wcześniej zapisana w localStorage.
 *   3. Wycofanie zgody przełącza GA z powrotem na `denied` (consent update).
 *
 * Klucz localStorage: `cookieConsentSettings` (JSON, zarządzany przez CookieConsent.tsx).
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface GoogleAnalyticsProps {
  measurementId?: string;
}

interface CookieConsentSettings {
  version: number;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
}

const STORAGE_KEY = 'cookieConsentSettings';

function readAnalyticsConsent(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as CookieConsentSettings;
    return parsed?.analytics === true;
  } catch {
    return false;
  }
}

const GoogleAnalytics = ({ measurementId }: GoogleAnalyticsProps) => {
  const GA_MEASUREMENT_ID = measurementId || process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || 'G-X0X7YH0VW9';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Aplikacja aktualnego stanu zgody (po załadowaniu lub po zmianie z bannera)
    const applyConsent = (granted: boolean) => {
      if (typeof window.gtag !== 'function') return;
      window.gtag('consent', 'update', {
        analytics_storage: granted ? 'granted' : 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      if (granted) {
        // Wyślij pageview dopiero po zgodzie (gtag w skrypcie ma send_page_view: false)
        window.gtag('config', GA_MEASUREMENT_ID, {
          page_title: document.title,
          page_location: window.location.href,
        });
      }
    };

    // Stan początkowy z localStorage
    applyConsent(readAnalyticsConsent());

    // Reaguj na zmianę zgody przez banner (w tej samej zakładce)
    const onConsentChange = (e: Event) => {
      const detail = (e as CustomEvent<{ analytics: boolean }>).detail;
      applyConsent(Boolean(detail?.analytics));
    };
    window.addEventListener('cookieConsentChanged', onConsentChange);

    // Reaguj na zmiany w innych zakładkach (storage event)
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        applyConsent(readAnalyticsConsent());
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('cookieConsentChanged', onConsentChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [GA_MEASUREMENT_ID]);

  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());

          // Consent Mode v2 — domyślnie wszystko zablokowane do czasu zgody
          gtag('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            wait_for_update: 500
          });

          gtag('config', '${GA_MEASUREMENT_ID}', {
            send_page_view: false,
            anonymize_ip: true,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
            cookie_flags: 'SameSite=Lax;Secure'
          });
        `}
      </Script>
    </>
  );
};

export default GoogleAnalytics;
